import type { OnInit } from '@angular/core';
import { EventEmitter, Inject, Output } from '@angular/core';
import { Component, Input } from '@angular/core';
import { ControlContainer, Validators } from '@angular/forms';
import type { Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import type { SubmittableFormGroup } from '../../generic/submittable-form-group';
import type { MinimalStop } from '../../stops/stop';
import { StopsService } from '../../stops/stops.service';

@Component({
  selector: 'mtx-stop-autocomplete',
  standalone: false,
  templateUrl: './stop-autocomplete.component.html',
  styleUrls: ['./stop-autocomplete.component.scss'],
})
export class StopAutocompleteComponent implements OnInit {

  @Input() public translateText?: string;

  @Input() public fgName?: string;

  @Input() public stopObj?: MinimalStop;

  @Output() public markAsTouched = new EventEmitter<void>();

  @Output() public stopAdded = new EventEmitter<MinimalStop>();

  @Output() public stopCleared = new EventEmitter<void>();

  public stopModel?: MinimalStop;

  public reqValFn = Validators.required;

  public constructor(@Inject(ControlContainer) private readonly controlContainer: ControlContainer,
    @Inject(StopsService) private readonly stopService: StopsService) {}

  public get fg(): SubmittableFormGroup | null {
    return this.fgName ? (this.controlContainer.control?.get(this.fgName) as SubmittableFormGroup) : null;
  }

  public ngOnInit(): void {
    if (this.stopObj) {
      // if an existing stop object is passed in, use it to populate autocomplete
      this.stopModel = this.stopObj;
    }
  }

  public addStop(stop: MinimalStop): void {
    this.stopModel = stop;
    if (stop?.id) {
      this.fg?.patchValue(stop);
      this.stopAdded.emit(stop);
    } else {
      this.fg?.patchValue({ id: null });
    }
  }

  public search = (text$: Observable<string>): Observable<MinimalStop[]> => text$.pipe(debounceTime(200),
    distinctUntilChanged(),
    switchMap((term: string) => this.stopService.searchMinimalStops({ name: term, sort: 'name' }).pipe(map(res => res.data), map((res: MinimalStop[]) => {
      if (term === '') {
        this.fg?.patchValue({ id: null, name: null });
        this.stopCleared.emit();
      }
      const self = (this.controlContainer.control?.value as MinimalStop).id;
      const existingIds = this.getSelectorIDs();
      const finalResult = res.filter(x => {
        const isInChildren =
                !existingIds.includes(x.id) && x.id !== self;

        return isInChildren;
      });

      return finalResult;
    }))));

  public formatter = (stop: { id: string; name: string }): string => stop.name ?? '';

  public onBlur(): void {
    if (!(this.fg?.value as MinimalStop)?.id) {
      this.stopModel = undefined;
    }
    this.fg?.controls.id.markAsTouched();
    this.fg?.controls.id.updateValueAndValidity({ onlySelf: true });
  }

  public onFocus(): void {
    this.markAsTouched.emit();
    if ((this.fg?.value as MinimalStop)?.id) {
      this.stopModel = undefined;
      this.fg?.setValue({ id: null, name: null });
      this.stopCleared.emit();
    }
  }

  private getSelectorIDs(): string[] {
    return [this.fg?.controls.id.value];
  }

}
